import React, {
  useEffect,
  useState,
} from "react";

import {
  NavLink,
  useLocation,
} from "react-router";

import {
  MdClose,
  MdDashboard,
  MdKeyboardArrowDown,
  MdMenu,
} from "react-icons/md";

import { ARABIC_LABELS } from "../../labels/Business/arabic";

import iconProfile from "../../assets/images/icon-profile.png";
import iconPassword from "../../assets/images/icon-password.png";
import iconContract from "../../assets/images/icon-contract.png";
import iconMatch from "../../assets/images/icon-match.png";
import iconMismatch from "../../assets/images/icon-mismatch.png";
import iconAttendance from "../../assets/images/icon-attendance.png";
import iconInvoice from "../../assets/images/icon-invoice.png";
import ninList from "../../assets/images/icon-ninlist.png";

import "./Sidebar.css";

const contractPaths = [
  "/business/subsidy/createSubsidy",
  "/business/subsidy/contractlist",
  "/business/subsidy/addJob",
  "/business/subsidy/joblist",
  "/business/subsidy/printContract",
];

const invoicePaths = [
  "/business/invoice/addinvoice",
  "/business/invoice/invoicelist",
  "/business/invoice/viewInvoiceDetail",
];

const SidebarBusinessMain = () => {
  const location = useLocation();
  const labels = ARABIC_LABELS;

  const [
    openSection,
    setOpenSection,
  ] = useState(null);

  const [
    isMobileDrawerOpen,
    setIsMobileDrawerOpen,
  ] = useState(false);

  /*
   * Preserve the current automatic opening of the
   * active contract or invoice section.
   *
   * Also close the mobile drawer after navigation.
   */
  useEffect(() => {
    setIsMobileDrawerOpen(false);

    if (
      contractPaths.some((path) =>
        location.pathname.startsWith(
          path
        )
      )
    ) {
      setOpenSection("contracts");
      return;
    }

    if (
      invoicePaths.some((path) =>
        location.pathname.startsWith(
          path
        )
      )
    ) {
      setOpenSection("invoices");
      return;
    }

    setOpenSection(null);
  }, [location.pathname]);

  /*
   * Allow the user to close the mobile drawer
   * with the Escape key.
   */
  useEffect(() => {
    if (!isMobileDrawerOpen) {
      return undefined;
    }

    const handleEscape = (
      event
    ) => {
      if (
        event.key === "Escape"
      ) {
        setIsMobileDrawerOpen(
          false
        );
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [isMobileDrawerOpen]);

  const toggleSection = (
    section
  ) => {
    setOpenSection(
      (currentSection) =>
        currentSection === section
          ? null
          : section
    );
  };

  const openMobileDrawer = () => {
    setIsMobileDrawerOpen(true);
  };

  const closeMobileDrawer = () => {
    setIsMobileDrawerOpen(false);
  };

  /*
   * Close only when a real navigation link is clicked.
   * Expand/collapse buttons continue to work normally.
   */
  const handleDrawerClick = (
    event
  ) => {
    if (
      event.target.closest?.("a")
    ) {
      closeMobileDrawer();
    }
  };

  const isContractSectionActive =
    contractPaths.some((path) =>
      location.pathname.startsWith(
        path
      )
    );

  const isInvoiceSectionActive =
    invoicePaths.some((path) =>
      location.pathname.startsWith(
        path
      )
    );

  return (
    <div className="dga-sidebar-shell">
      {/* Mobile menu button */}
      <button
        type="button"
        className="dga-sidebar__mobile-toggle"
        onClick={openMobileDrawer}
        aria-label="فتح القائمة الرئيسية"
        aria-controls="business-navigation-drawer"
        aria-expanded={
          isMobileDrawerOpen
        }
        title="القائمة الرئيسية"
      >
        <MdMenu aria-hidden="true" />
      </button>

      {/* Mobile background overlay */}
      <button
        type="button"
        className={`dga-sidebar__overlay ${
          isMobileDrawerOpen
            ? "show"
            : ""
        }`}
        onClick={closeMobileDrawer}
        aria-label="إغلاق القائمة الرئيسية"
        tabIndex={
          isMobileDrawerOpen
            ? 0
            : -1
        }
      />

      {/* Desktop sidebar / mobile drawer */}
      <div
        id="business-navigation-drawer"
        className={`dga-sidebar__drawer ${
          isMobileDrawerOpen
            ? "open"
            : ""
        }`}
        onClick={handleDrawerClick}
      >
        <div className="dga-sidebar__drawer-header">
          <strong>
            القائمة الرئيسية
          </strong>

          <button
            type="button"
            className="dga-sidebar__drawer-close"
            onClick={
              closeMobileDrawer
            }
            aria-label="إغلاق القائمة الرئيسية"
            title="إغلاق"
          >
            <MdClose
              aria-hidden="true"
            />
          </button>
        </div>

        <aside
          className="dga-sidebar"
          aria-label="القائمة الرئيسية"
        >
          <nav className="dga-sidebar__navigation">
            <ul className="dga-sidebar__list">
              <li className="dga-sidebar__item">
                <NavLink
                  to="/business/dashboard"
                  className={({
                    isActive,
                  }) =>
                    `dga-sidebar__link ${
                      isActive
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <span
                    className="dga-sidebar__icon"
                    aria-hidden="true"
                  >
                    <MdDashboard />
                  </span>

                  <span className="dga-sidebar__label">
                    {
                      labels.dashboard
                    }
                  </span>
                </NavLink>
              </li>

              <li className="dga-sidebar__item">
                <NavLink
                  to="/business/editProfileBusiness"
                  className={({
                    isActive,
                  }) =>
                    `dga-sidebar__link ${
                      isActive
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <SidebarImageIcon
                    src={iconProfile}
                  />

                  <span className="dga-sidebar__label">
                    {
                      labels.changeprofile
                    }
                  </span>
                </NavLink>
              </li>

              <li className="dga-sidebar__item">
                <NavLink
                  to="/business/changePasswordBusiness"
                  className={({
                    isActive,
                  }) =>
                    `dga-sidebar__link ${
                      isActive
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <SidebarImageIcon
                    src={iconPassword}
                  />

                  <span className="dga-sidebar__label">
                    {
                      labels.changepassword
                    }
                  </span>
                </NavLink>
              </li>

              <li
                className={`dga-sidebar__item dga-sidebar__item--expandable ${
                  openSection ===
                  "contracts"
                    ? "open"
                    : ""
                }`}
              >
                <button
                  type="button"
                  className={`dga-sidebar__link dga-sidebar__button ${
                    isContractSectionActive
                      ? "section-active"
                      : ""
                  }`}
                  onClick={() =>
                    toggleSection(
                      "contracts"
                    )
                  }
                  aria-expanded={
                    openSection ===
                    "contracts"
                  }
                >
                  <SidebarImageIcon
                    src={iconContract}
                  />

                  <span className="dga-sidebar__label">
                    {
                      labels.contract
                    }
                  </span>

                  <MdKeyboardArrowDown
                    className="dga-sidebar__arrow"
                    aria-hidden="true"
                  />
                </button>

                <div className="dga-sidebar__submenu-wrapper">
                  <ul className="dga-sidebar__submenu">
                    <li>
                      <NavLink
                        to="/business/subsidy/createSubsidy"
                        className={({
                          isActive,
                        }) =>
                          isActive
                            ? "active"
                            : ""
                        }
                      >
                        {
                          labels.createcontract
                        }
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/business/subsidy/contractlist"
                        className={({
                          isActive,
                        }) =>
                          isActive
                            ? "active"
                            : ""
                        }
                      >
                        {
                          labels.contractlisth
                        }
                      </NavLink>
                    </li>
                  </ul>
                </div>
              </li>

              <li className="dga-sidebar__item">
                <NavLink
                  to="/business/subsidy/linkcustomer"
                  className={({
                    isActive,
                  }) =>
                    `dga-sidebar__link ${
                      isActive
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <SidebarImageIcon
                    src={iconMatch}
                  />

                  <span className="dga-sidebar__label">
                    {labels.linkpc}
                  </span>
                </NavLink>
              </li>

              <li className="dga-sidebar__item">
                <NavLink
                  to="/business/subsidy/removecustomer"
                  className={({
                    isActive,
                  }) =>
                    `dga-sidebar__link ${
                      isActive
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <SidebarImageIcon
                    src={
                      iconMismatch
                    }
                  />

                  <span className="dga-sidebar__label">
                    {
                      labels.removepc
                    }
                  </span>
                </NavLink>
              </li>

              <li className="dga-sidebar__item">
                <NavLink
                  to="/business/subsidy/addattendance"
                  className={({
                    isActive,
                  }) =>
                    `dga-sidebar__link ${
                      isActive
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <SidebarImageIcon
                    src={
                      iconAttendance
                    }
                  />

                  <span className="dga-sidebar__label">
                    {
                      labels.createattendance
                    }
                  </span>
                </NavLink>
              </li>

              <li
                className={`dga-sidebar__item dga-sidebar__item--expandable ${
                  openSection ===
                  "invoices"
                    ? "open"
                    : ""
                }`}
              >
                <button
                  type="button"
                  className={`dga-sidebar__link dga-sidebar__button ${
                    isInvoiceSectionActive
                      ? "section-active"
                      : ""
                  }`}
                  onClick={() =>
                    toggleSection(
                      "invoices"
                    )
                  }
                  aria-expanded={
                    openSection ===
                    "invoices"
                  }
                >
                  <SidebarImageIcon
                    src={iconInvoice}
                  />

                  <span className="dga-sidebar__label">
                    {
                      labels.invsection
                    }
                  </span>

                  <MdKeyboardArrowDown
                    className="dga-sidebar__arrow"
                    aria-hidden="true"
                  />
                </button>

                <div className="dga-sidebar__submenu-wrapper">
                  <ul className="dga-sidebar__submenu">
                    <li>
                      <NavLink
                        to="/business/invoice/addinvoice"
                        className={({
                          isActive,
                        }) =>
                          isActive
                            ? "active"
                            : ""
                        }
                      >
                        {
                          labels.addinvoice
                        }
                      </NavLink>
                    </li>

                    <li>
                      <NavLink
                        to="/business/invoice/invoicelist"
                        className={({
                          isActive,
                        }) =>
                          isActive
                            ? "active"
                            : ""
                        }
                      >
                        {
                          labels.invoicelist
                        }
                      </NavLink>
                    </li>
                  </ul>
                </div>
              </li>

              <li className="dga-sidebar__item">
                <NavLink
                  to="/business/subsidy/totalemployes"
                  className={({
                    isActive,
                  }) =>
                    `dga-sidebar__link ${
                      isActive
                        ? "active"
                        : ""
                    }`
                  }
                >
                  <SidebarImageIcon
                    src={ninList}
                  />

                  <span className="dga-sidebar__label">
                    {
                      labels.employeoffer
                    }
                  </span>
                </NavLink>
              </li>
            </ul>
          </nav>
        </aside>
      </div>
    </div>
  );
};

const SidebarImageIcon = ({
  src,
}) => {
  return (
    <span
      className="dga-sidebar__icon"
      aria-hidden="true"
    >
      <img src={src} alt="" />
    </span>
  );
};

export default SidebarBusinessMain;